import { useGetRiskFactorsV1RiskFactorsGet } from '@/api/generated-catch-backend/risk-factors/risk-factors';
import { ROUTE_RISK_ANALYSIS } from '@/constants/routes';
import { PortableText } from '@portabletext/react';
import { useNavigate } from 'react-router-dom';
import { RiskFactorCard } from './components/risk-factor-card';

export default function RiskFactors() {
  const { data: riskFactorsData, isLoading, error } = useGetRiskFactorsV1RiskFactorsGet();
  const navigate = useNavigate();

  if (isLoading) {
    return (
      <div className="flex w-full flex-col gap-[14px] rounded-[10px] bg-[#f0f5f6]">
        <div>
          <h2 className="mobile-h3-primary text-primary font-bold">Your Top Risk Factors</h2>
          <div className="mt-2 h-4 w-96 animate-pulse rounded bg-gray-200" />
        </div>

        <div className="flex w-full max-w-[335px] gap-3 overflow-x-auto pt-2 pb-2">
          {[1, 2, 3].map((index) => (
            <div key={`skeleton-${index}`} className="w-[140px] flex-shrink-0 md:max-w-[240px]">
              <div className="h-[183px] animate-pulse rounded bg-gray-200" />
            </div>
          ))}
        </div>

        <div className="mt-4 flex justify-center">
          <div className="h-10 w-[293px] animate-pulse rounded-full bg-gray-200" />
        </div>
      </div>
    );
  }

  if (error || !riskFactorsData) {
    return (
      <div className="flex w-full flex-col gap-[14px] rounded-[10px] bg-[#f0f5f6]">
        <div>
          <h2 className="mobile-h3-primary text-primary font-bold">Your Top Risk Factors</h2>
          <p className="body-regular text-primary mt-2">Unable to load risk factors at this time.</p>
        </div>
      </div>
    );
  }

  const { topFactors, riskFactors, summaryText } = riskFactorsData;
  const topRiskFactors = [
    {
      ...topFactors.topDecreasing,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ...riskFactors.find((factor) => factor.slug === topFactors.topDecreasing.slug)!,
      type: 'decreasing' as const,
    },
    {
      ...topFactors.topIncreasing,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ...riskFactors.find((factor) => factor.slug === topFactors.topIncreasing.slug)!,
      type: 'increasing' as const,
    },
    {
      ...topFactors.topModifiable,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ...riskFactors.find((factor) => factor.slug === topFactors.topModifiable.slug)!,
      type: 'modifiable' as const,
    },
  ];

  return (
    <div className="risk-cards-container max-w-[335px] md:max-w-none">
      <div className="action-item-text-width">
        <h2 className="mobile-h3-primary text-primary text-left font-bold">Your Top Risk Factors</h2>
        <PortableText
          value={[...summaryText]}
          components={{
            block: {
              normal: ({ children }) => <p className="body-regular text-primary">{children}</p>,
            },
            marks: {
              strong: ({ children }) => <strong className="text-bold">{children}</strong>,
            },
          }}
        />
      </div>

      <div className="flex w-full gap-3 overflow-x-auto pt-2 pb-2">
        {topRiskFactors.map((factor, index) => (
          <RiskFactorCard key={`${factor.type}-${index}`} riskFactor={factor} type={factor.type} text={factor.text} />
        ))}
      </div>

      <div className="cta-button-container">
        <button
          onClick={() => {
            navigate(ROUTE_RISK_ANALYSIS);
          }}
          className="cta-button body-meta"
        >
          See all Risk Factors
        </button>
      </div>
    </div>
  );
}
